<template>
    <section>
        <Header :title="isLoading || !organisation ? (listOrganisation ? listOrganisation.name : 'Dienstverlener') : organisation.name"
                show-back-button></Header>
        <v-container>
            <v-breadcrumbs :items="breadcrumbs" class="px-0"></v-breadcrumbs>
            <p v-if="isLoading" class="text-center my-7">
                <v-icon large color="primary">fal fa-spinner-third fa-spin</v-icon>
            </p>
            <v-alert v-if="notFound" color="warning">
                <v-icon class="mr-3">fal fa-exclamation-triangle</v-icon>
                <span>De dienstverlener kon niet worden gevonden.</span>
            </v-alert>
            <div v-if="organisation">
                <v-card class="organisation-card organisation-detail-card mb-5" elevation="0">
                    <v-card-text class="pa-5 pa-md-8">
                        <div class="d-flex flex-column flex-md-row">
                            <div class="order-2 order-md-1 organisation-title">
                                <h2 class="text-h4 mb-3 primary--text">
                                    {{ organisation.name }}
                                </h2>
                                <ul class="list-unstyled details">
                                    <li class="mb-2">
                                        <v-icon
                                            small
                                            color="secondary"
                                            class="mr-1 fa-fw"
                                        >
                                            far fa-envelope
                                        </v-icon>
                                        {{ organisation.email }}
                                    </li>
                                    <li class="mb-2">
                                        <v-icon
                                            small
                                            color="secondary"
                                            class="mr-1 fa-fw"
                                        >
                                            far fa-phone
                                        </v-icon>
                                        {{ organisation.phoneNumber }}
                                    </li>
                                    <li class="mb-2">
                                        <v-icon
                                            small
                                            color="secondary"
                                            class="mr-1 fa-fw"
                                        >
                                            far fa-globe
                                        </v-icon>
                                        <a :href="websiteUrl" target="_blank">{{ organisation.website }}</a>
                                    </li>
                                    <li class="mb-2" v-if="planning">
                                        <v-icon
                                            small
                                            color="secondary"
                                            class="mr-1 fa-fw"
                                        >
                                            far fa-calendar
                                        </v-icon>
                                        {{ planning }}
                                    </li>
                                    <li class="mb-2" v-if="organisation.totalCosts">
                                        <v-icon
                                            small
                                            color="secondary"
                                            class="mr-1 fa-fw"
                                        >
                                            far fa-euro-sign
                                        </v-icon>
                                        {{ formattedCosts }} per deelname (incl. btw)
                                    </li>

                                </ul>
                                <v-btn
                                    color="primary"
                                    rounded
                                    class="elevation-0 mt-2"
                                    :to="{ name: 'RequestForm', params: { uuid: organisation.uuid } }"
                                >
                                    <v-icon left small>far fa-paper-plane</v-icon>
                                    Activiteit aanvragen
                                </v-btn>
                            </div>
                            <v-avatar
                                size="250"
                                class="order-1 order-md-2 ml-md-auto mb-4 mb-md-0 avatar-contain"
                                v-if="organisation.logo"
                                rounded="0"
                            >
                                <img
                                    :src="organisation.logo.url"
                                    :alt="organisation.name"
                                >
                            </v-avatar>
                        </div>
                    </v-card-text>
                </v-card>
                <v-row class="align-start my-0 pb-4">
                    <v-col class="col-12 col-md-7 py-0 mb-4 mb-md-0">
                        <v-card elevation="0" v-if="organisation.externIntern || organisation.finalRegistrationDate || organisation.location || organisation.minimalParticipation || organisation.duration">
                            <v-card-title class="px-6 pt-6">Algemeen</v-card-title>
                            <v-card-text class="px-6 pb-6">
                                <v-row>
                                    <v-col cols="12" md="7">
                                        <div v-if="organisation.externIntern">
                                            <strong>Extern / Intern:</strong>
                                            <p>{{ organisation.externIntern }}</p>
                                        </div>
                                        <div v-if="organisation.finalRegistrationDate">
                                            <strong>Uiterlijke aanmelddatum:</strong>
                                            <p>{{ organisation.finalRegistrationDate|luxon }}</p>
                                        </div>
                                        <div v-if="organisation.location">
                                            <strong>Locatie:</strong>
                                            <p>{{ organisation.location }}</p>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="5">
                                        <div v-if="organisation.minimalParticipation">
                                            <strong>Minimale deelname:</strong>
                                            <p>{{ organisation.minimalParticipation }}</p>
                                        </div>
                                        <div v-if="organisation.duration">
                                            <strong>Duur (in minuten):</strong>
                                            <p>{{ organisation.duration }}</p>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                        <v-card v-if="organisation.mainActivity" elevation="0" class="mt-5">
                            <v-card-title class="px-6 pt-6">Hoofdactiviteit bedrijf</v-card-title>
                            <v-card-text class="px-6 pb-6">
                                <p style="white-space: pre-line;">{{ organisation.mainActivity }}</p>
                            </v-card-text>
                        </v-card>
                        <v-card v-if="organisation.introductionText" elevation="0" class="mt-5">
                            <v-card-title class="px-6 pt-6">Introductie/presentatietekst</v-card-title>
                            <v-card-text class="px-6 pb-6">
                                <p style="white-space: pre-line;">{{ organisation.introductionText }}</p>
                            </v-card-text>
                        </v-card>
                        <div v-if="organisation.video" class="mb-4 mt-4 mb-md-0">
                            <VimeoVideo v-if="organisation.video.provider === 'vimeo'"
                                        :video-id="organisation.video.id"></VimeoVideo>
                            <YoutubeVideo v-if="organisation.video.provider === 'youtube'"
                                          :video-id="organisation.video.id"></YoutubeVideo>
                        </div>
                    </v-col>
                    <v-col class="col-12 col-md-4 offset-md-1 py-0">
                        <v-card
                            color="#d9edf0"
                            v-for="facet in mappedFacets"
                            :key="facet.id"
                            elevation="0"
                            class="mb-3"
                        >
                            <v-card-title class="ma-0 text-h6 px-6 pt-6 pb-0">{{ facet.name }}</v-card-title>
                            <v-card-text class="px-5 pb-5">
                                <v-chip
                                    color="primary"
                                    v-for="choice in facet.choices"
                                    :key="choice.id"
                                    class="ma-1"
                                    small
                                >
                                    {{ choice.value }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </v-container>
    </section>
</template>

<script>
import Header from "@/components/layout/Header";
import {mapState} from "vuex";
import VimeoVideo from "../components/video/VimeoVideo";
import YoutubeVideo from "../components/video/YoutubeVideo";
import {formatCosts, formatPlanning} from "@/utils/formatters";

export default {
    name: 'Organisation',
    components: {
        YoutubeVideo,
        VimeoVideo,
        Header,
    },
    props: {
        uuid: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        isLoading: true,
        notFound: false,
        organisation: null,
    }),
    computed: {
        breadcrumbs () {
            const crumbs = [];

            // De Dashboard-kruimel verwijst naar een login-only pagina; verberg hem voor uitgelogde bezoekers.
            if (this.isLoggedIn) {
                crumbs.push({
                    text: 'Dashboard',
                    disabled: false,
                    exact: true,
                    to: { name: 'Home' },
                });
            }

            crumbs.push({
                text: 'Dienstverleners',
                disabled: false,
                exact: true,
                to: { name: 'Organisations' },
            });

            crumbs.push({
                text: 'Dienstverlener',
                disabled: true,
            });

            return crumbs;
        },
        listOrganisation() {
            return this.organisations.find(organisation => organisation.uuid === this.uuid);
        },
        mappedFacets() {
            if (!this.organisation) {
                return [];
            }

            let facets = [];

            this.organisation.facetChoices.forEach(facetChoice => {
                let facet = facets.find(f => f.id === facetChoice.facet.id);
                if (!facet) {
                    facet = JSON.parse(JSON.stringify(facetChoice.facet));
                    facet.choices = [];
                    facets.push(facet);
                }
                facet.choices.push({
                    id: facetChoice.id,
                    value: facetChoice.value,
                    sortId: facetChoice.sortId,
                });
            });

            facets.sort((a, b) => a.sortId - b.sortId);
            facets.forEach(facet => {
                facet.choices.sort((a, b) => a.sortId - b.sortId);
            });

            return facets;
        },
        planning() {
            return formatPlanning(this.organisation);
        },
        formattedCosts() {
            return formatCosts(this.organisation.totalCosts);
        },
        websiteUrl() {
            if (this.organisation.website.substring(0,8) === 'https://' || this.organisation.website.substring(0,7) === 'http://') {
                return this.organisation.website;
            } else {
                return 'https://'+this.organisation.website;
            }
        },
        ...mapState({
            organisations: state => state.organisation.organisations,
            loadedOrganisations: state => state.organisation.loadedOrganisations,
            isLoggedIn: state => state.auth.isLoggedIn,
        }),
    },
    mounted() {
        this.$store.dispatch('organisation/getOrganisation', this.uuid)
            .then(organisation => {
                this.organisation = organisation;
            })
            .catch(e => {
                console.error(e);
                this.$store.dispatch('showSnackbar', {color: 'error', text: 'Dienstverlener kon niet worden opgehaald'});
                const loadedOrganisation = this.loadedOrganisations.find(organisation => organisation.uuid === this.uuid);
                if (loadedOrganisation) {
                    this.organisation = loadedOrganisation;
                } else if (!this.listOrganisation) {
                    this.notFound = true;
                    this.isLoading = false;
                }
            })
            .finally(() => {
                if (this.organisation) {
                    this.isLoading = false;
                }
            });
    },
}
</script>
